import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import ImageListItem from "@mui/material/ImageListItem";
import Avatar from "@mui/material/Avatar";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Logout from "@mui/icons-material/Logout";
import ListItemButton from "@mui/material/ListItemButton";
import MenuIcon from "@mui/icons-material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import LockResetIcon from "@mui/icons-material/LockReset";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import "./MainLayout.css";
import { Outlet } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/auth";
import { getUserData } from "../utils/utilities";
import CommonDialog from "./CommonDialog";

const protectedModules = [
  {
    label: "Expertise Management",
    route: "/expertises",
  },
  {
    label: "Language Management",
    route: "/languages",
  },
  {
    label: "Nationality Management",
    route: "/nationalities",
  },
  {
    label: "Religion Management",
    route: "/religions",
  },
  {
    label: "Tools And Tech",
    route: "/tools",
  },
];

const drawerWidth = 240;

export default function MainLayout(props) {
  let navigate = useNavigate();
  const { window } = props;
  const auth = useAuth();
  const location = useLocation();
  const [modules, setModules] = React.useState([
    {
      label: "Profile Management",
      route: "/employee-profiles/list",
    },
  ]);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openLogoutModal, setOpenLogoutModal] = React.useState(false);
  const open = Boolean(anchorEl);

  const openProfileMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const closeProfileMenu = () => {
    setAnchorEl(null);
  };

  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const naviagateToPage = (event, index) => {
    setSelectedIndex(index);
    let module = modules[index];
    const targetRoute = module.route;
    
    const getBasePath = (path) => path.split('/').slice(0, 2).join('/');
    const targetBase = getBasePath(targetRoute);
    const currentBase = getBasePath(location.pathname);

    const shouldClearTargetState = (targetBase !== currentBase) || (location.pathname === targetRoute);

    const routeToPrefix = {
      "/admin-users": "adminUser_",
      "/companies": "company_",
      "/employee-profiles/list": "employee_",
      "/expertises": "expertise_",
      "/languages": "language_",
      "/nationalities": "nationality_",
      "/religions": "religion_",
      "/tools": "tools_",
      "/users": "user_"
    };
    
    const targetPrefix = routeToPrefix[targetRoute];

    // Clear persisted states appropriately
    Object.keys(sessionStorage).forEach(key => {
      const isKnown = Object.values(routeToPrefix).some(p => key.startsWith(p));
      if (isKnown) {
        if (targetPrefix && key.startsWith(targetPrefix)) {
          if (shouldClearTargetState) {
            sessionStorage.removeItem(key);
          }
        } else {
          sessionStorage.removeItem(key);
        }
      }
    });

    if (location.pathname === targetRoute && shouldClearTargetState) {
      window.location.reload();
    } else {
      navigate(targetRoute);
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    auth.logout();
  };

  React.useEffect(() => {
    let index = modules.findIndex((m) => {
      return m.route === location.pathname;
    });

    if (index !== -1) {
      setSelectedIndex(index);
    }
  }, [modules]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <Toolbar>
        <ImageListItem
          sx={{
            mx: "auto",
            my: 3,
          }}
        >
          <img
            src="/Nexuslink Logo.png"
            alt="Nexuslink Services Logo"
            loading="lazy"
            height={70}
          />
        </ImageListItem>
      </Toolbar>
      <Divider />
      <List component="nav">
        {modules.map((module, index) => (
          <ListItemButton
            key={module.route}
            selected={selectedIndex === index}
            onClick={(event) => naviagateToPage(event, index)}
            className="side-nav-link"
          >
            <ListItemText
              sx={{
                height: "100%",
                width: "100%",
              }}
            >
              <Typography variant="body1" noWrap sx={{ fontSize: "14px" }}>
                {selectedIndex === index ? <b>{module.label}</b> : module.label}
              </Typography>
            </ListItemText>
          </ListItemButton>
        ))}
      </List>
    </div>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  let role = getUserData().role;

  React.useEffect(() => {
    if (role == "SuperAdmin") {
      setModules((prevState) => [
        { label: "Admin Users", route: "/admin-users" },
        ...prevState,
        {
          label: "Company Management",
          route: "/companies",
        },
        ...protectedModules,
        { label: "User Management", route: "/users" },
      ]);
    } else if (role == "CompanyAdmin" || role == "Company") {
      setModules((prevState) => [
        ...prevState,
        ...protectedModules,
        { label: "User Management", route: "/users" },
      ]);
    } else {
      setModules((prevState) => [...prevState, ...protectedModules]);
    }
  }, []);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="body1"
            noWrap
            component="div"
            sx={{ flexGrow: 1 }}
          >
            <b>NLS Employee Profile Portal</b>
          </Typography>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ marginTop: "10px" }}>{getUserData().name || ""}</Box>
            <Tooltip title="Account settings">
              <IconButton
                onClick={openProfileMenu}
                size="small"
                sx={{ ml: 1 }}
                aria-controls={open ? "account-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
              >
                <Avatar
                  alt="Cindy Baker"
                  variant="dense"
                  src="/static/images/avatar/3.jpg"
                  sx={{ bgcolor: "grey", width: 32, height: 32 }}
                >
                  {(getUserData().name || "")?.slice(0, 1)?.toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={closeProfileMenu}
            onClick={closeProfileMenu}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                mt: 1.5,
                "& .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem
              onClick={() => {
                navigate("/user-profile");
                setSelectedIndex(null);
              }}
            >
              <ListItemIcon>
                <AccountCircleIcon />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate("/change-password");
                setSelectedIndex(null);
              }}
            >
              <ListItemIcon>
                <LockResetIcon />
              </ListItemIcon>
              Change Password
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              setOpenLogoutModal(true);
              closeProfileMenu();
            }}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { sm: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: "#f4f6f9",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
      <CommonDialog
        title="Logout Confirmation"
        open={openLogoutModal}
        onClose={() => setOpenLogoutModal(false)}
        onSubmit={handleLogout}
        submitLabel="Logout"
        submitColor="error"
      >
        <Typography>Are you sure you want to logout?</Typography>
      </CommonDialog>
    </Box>
  );
}
